export const DTE_TEMPLATE_LAYOUTS = Object.freeze([
  { key: "estandar", label: "Estandar", renderKey: "standard" },
  { key: "completo", label: "Completo", renderKey: "full" },
  { key: "compacto", label: "Compacto", renderKey: "compact" },
]);
export const DEFAULT_BRANCH_DTE_TEMPLATE_LAYOUT_KEY = "estandar";

export const DTE_TEMPLATE_LAYOUT_OPTIONS = DTE_TEMPLATE_LAYOUTS.map((layout) => ({
  key: layout.key,
  label: layout.label,
}));

export const DTE_TEMPLATE_FONT_SIZE_PRESETS = Object.freeze([
  { value: "small", label: "Small" },
  { value: "medium", label: "Medium" },
  { value: "large", label: "Large" },
]);

export const DTE_TEMPLATE_SIZE_LIMITS = Object.freeze({
  title: { min: 12, max: 30 },
  text: { min: 9, max: 20 },
  detail: { min: 8, max: 18 },
  gradient: { min: 0, max: 100 },
  radius: { min: 0, max: 32 },
  paginationLines: { min: 10, max: 80 },
});

export const DTE_TEMPLATE_TITLE_FONT_SIZE_MAP = Object.freeze({
  small: 16,
  medium: 18,
  large: 21,
});

export const DTE_TEMPLATE_TEXT_FONT_SIZE_MAP = Object.freeze({
  small: 11,
  medium: 12,
  large: 13,
});
export const DTE_TEMPLATE_DETAIL_FONT_SIZE_MAP = Object.freeze({
  small: 10,
  medium: 11,
  large: 12,
});

export const DTE_TEMPLATE_CARD_RADIUS_PRESET_MAP = Object.freeze({
  estandar: Object.freeze({ small: 8, medium: 14, large: 20 }),
  completo: Object.freeze({ small: 8, medium: 14, large: 20 }),
  compacto: Object.freeze({ small: 4, medium: 7, large: 12 }),
});

export const DTE_TEMPLATE_TABLE_RADIUS_PRESET_MAP = Object.freeze({
  estandar: Object.freeze({ small: 0, medium: 8, large: 14 }),
  completo: Object.freeze({ small: 0, medium: 8, large: 14 }),
  compacto: Object.freeze({ small: 0, medium: 8, large: 14 }),
});

export const DTE_TEMPLATE_PAGINATION_DEFAULTS = Object.freeze({
  estandar: Object.freeze({
    linesFirstPage: 41,
    linesDetailPage: 35,
    linesLastPage: 35,
    showLineGuide: false,
    hideRelatedDocumentsPreview: false,
  }),
  completo: Object.freeze({
    linesFirstPage: 41,
    linesDetailPage: 34,
    linesLastPage: 34,
    showLineGuide: false,
    hideRelatedDocumentsPreview: false,
  }),
  compacto: Object.freeze({
    linesFirstPage: 47,
    linesDetailPage: 42,
    linesLastPage: 42,
    showLineGuide: false,
    hideRelatedDocumentsPreview: false,
  }),
});

function clone(value) {
  return JSON.parse(JSON.stringify(value));
}

function ensurePlainObject(value, label) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error(`${label} debe ser un objeto valido.`);
  }
}

export function getDteTemplateLayoutByKey(layoutKey = "estandar") {
  return (
    DTE_TEMPLATE_LAYOUTS.find((layout) => layout.key === layoutKey) ||
    DTE_TEMPLATE_LAYOUTS.find(
      (layout) => layout.key === DEFAULT_BRANCH_DTE_TEMPLATE_LAYOUT_KEY
    ) ||
    DTE_TEMPLATE_LAYOUTS[0]
  );
}

export function mapDteTemplateLayoutToRenderKey(layoutKey = "estandar") {
  return getDteTemplateLayoutByKey(layoutKey).renderKey;
}

export function mapDteRenderLayoutToTemplateKey(renderKey = "standard") {
  const layout = DTE_TEMPLATE_LAYOUTS.find((item) => item.renderKey === renderKey);
  return layout ? layout.key : DEFAULT_BRANCH_DTE_TEMPLATE_LAYOUT_KEY;
}

export function normalizeBranchDteTemplateLayoutKey(value, fallback = "") {
  const normalized = String(value || "").trim().toLowerCase();
  if (DTE_TEMPLATE_LAYOUTS.some((layout) => layout.key === normalized)) {
    return normalized;
  }

  const fallbackKey = String(fallback || "").trim().toLowerCase();
  return DTE_TEMPLATE_LAYOUTS.some((layout) => layout.key === fallbackKey)
    ? fallbackKey
    : "";
}

export function isBranchDteTemplateV2Enabled(branch = {}, fallback = 0) {
  const value = branch ? branch.UseDteTemplateV2 : undefined;
  if (value === undefined || value === null || value === "") {
    return Number(fallback) !== 0;
  }
  return Number(value) !== 0;
}

export function getBranchConfiguredDteTemplateLayoutKey(branch = {}) {
  return normalizeBranchDteTemplateLayoutKey(
    branch ? branch.DteTemplateDefaultLayoutKey : "",
    ""
  );
}

export function hasBranchConfiguredDteTemplateSelection(branch = {}) {
  return (
    isBranchDteTemplateV2Enabled(branch, 0) &&
    Boolean(getBranchConfiguredDteTemplateLayoutKey(branch))
  );
}

export async function resolveBranchConfiguredDteTemplate(api, branch = {}) {
  const layoutKey = getBranchConfiguredDteTemplateLayoutKey(branch);
  const branchId = Number(branch && branch.ID_Branch);
  const hasSelection = hasBranchConfiguredDteTemplateSelection(branch);

  if (
    !hasSelection ||
    !Number.isInteger(branchId) ||
    branchId <= 0 ||
    !api ||
    !api.branchDteTemplateSettings
  ) {
    return {
      eligible: false,
      hasSelection,
      isCustom: false,
      layoutKey,
    };
  }

  try {
    const response = await api.branchDteTemplateSettings.getSettings(branchId, layoutKey);
    const isCustom = Boolean(response && response.data && response.data.isCustom);
    return {
      eligible: isCustom,
      hasSelection,
      isCustom,
      layoutKey,
      response: response && response.data ? response.data : null,
    };
  } catch (error) {
    return {
      eligible: false,
      hasSelection,
      isCustom: false,
      layoutKey,
      response: null,
    };
  }
}

export function getDefaultDteTemplateSettings(layoutKey = "estandar") {
  const resolvedLayoutKey = getDteTemplateLayoutByKey(layoutKey).key;

  const defaultTableBorderColor =
    resolvedLayoutKey === "estandar"
      ? "#000000"
      : resolvedLayoutKey === "completo"
        ? "#B8CADE"
        : "#C7D5E5";
  const cardRadiusPreset = "medium";
  const tableRadiusPreset = "small";

  return clone({
    theme: {
      headerColor: "#2F5D8C",
      gradientColor: "#2F5D8C",
      gradientStrength: 22,
      bannerFullPage: false,
      hideAdvertising: false,
      hideAdvertisingOnPrint: true,
      hideAdvertisingOnPdfDownload: true,
      tableBorderColor: defaultTableBorderColor,
      cardBorderRadiusPreset: cardRadiusPreset,
      cardBorderRadiusPx: DTE_TEMPLATE_CARD_RADIUS_PRESET_MAP[resolvedLayoutKey][cardRadiusPreset],
      tableBorderRadiusPreset: tableRadiusPreset,
      tableBorderRadiusPx: DTE_TEMPLATE_TABLE_RADIUS_PRESET_MAP[resolvedLayoutKey][tableRadiusPreset],
      titleFontSizePreset: "medium",
      titleFontSizePx: DTE_TEMPLATE_TITLE_FONT_SIZE_MAP.medium,
      textFontSizePreset: "medium",
      textFontSizePx: DTE_TEMPLATE_TEXT_FONT_SIZE_MAP.medium,
      detailFontSizePreset: "medium",
      detailFontSizePx: DTE_TEMPLATE_DETAIL_FONT_SIZE_MAP.medium,
    },
    pagination: clone(DTE_TEMPLATE_PAGINATION_DEFAULTS[resolvedLayoutKey]),
    visibility: {
      issuer: {
        address: true,
        businessLine: true,
        phone: true,
        email: true,
      },
      receiver: {
        customerCode: true,
        sellerCode: true,
        sellerName: true,
        contact: true,
        entity: true,
        currency: true,
        zone: true,
        paymentStatus: true,
        economicActivity: true,
      },
      document: {
        dueDate: true,
      },
      detail: {
        itemType: true,
        code: true,
        alternateCode: true,
        unit: true,
        discount: true,
        nonSubjectSales: true,
        exemptSales: true,
        nonTaxableSales: true,
      },
      totals: {
        discounts: true,
        retention: true,
        perception: true,
        nonTaxable: true,
        nonSubject: true,
        exempt: true,
        fovial: true,
        cotrans: true,
      },
    },
    labels: {
      itemNumber: "No. Item",
      quantity: "Cantidad",
      description: "Descripcion",
      mainAmount: "Ventas gravadas",
      unitPrice: "Precio unitario",
      discount: "Descuento",
      phone: "Tel.",
      email: "Correo",
      itemType: "Tipo item",
      code: "Codigo",
      alternateCode: "Codigo alterno",
      unit: "Unidad",
      customerCode: "Codigo cliente",
      sellerCode: "Codigo vendedor",
      sellerName: "Nombre vendedor",
      contact: "Contacto",
      entity: "Entidad",
      currency: "Moneda",
      zone: "Zona",
      paymentStatus: "Estado de la factura",
      economicActivity: "Actividad economica",
      dueDate: "Fecha vencimiento",
      subtotal: "Sub total ventas gravadas",
      discounts: "Total descuentos",
      nonSubjectSales: "Ventas no sujetas",
      exemptSales: "Ventas exentas",
      nonTaxableSales: "No gravadas",
      retention: "Retencion",
      perception: "Percepcion",
      nonTaxable: "No gravadas",
      nonSubject: "No sujetas",
      exempt: "Exentas",
      fovial: "Fovial",
      cotrans: "Cotrans",
    },
  });
}

function sanitizeColor(value) {
  const normalized = String(value || "").trim();
  if (!/^#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$/.test(normalized)) {
    throw new Error("El color de encabezado no es valido.");
  }
  return normalized.toUpperCase();
}

function sanitizePreset(value, fallback) {
  const normalized = String(value || fallback || "").trim().toLowerCase();
  const allowed = DTE_TEMPLATE_FONT_SIZE_PRESETS.map((preset) => preset.value);
  if (!allowed.includes(normalized)) {
    throw new Error("El preset de tamano no es valido.");
  }
  return normalized;
}

function sanitizeRadiusPreset(value, fallback) {
  return sanitizePreset(value, fallback);
}

function getRadiusPresetMap(radiusType, layoutKey = "estandar") {
  const resolvedLayoutKey = getDteTemplateLayoutByKey(layoutKey).key;
  return radiusType === "card"
    ? DTE_TEMPLATE_CARD_RADIUS_PRESET_MAP[resolvedLayoutKey]
    : DTE_TEMPLATE_TABLE_RADIUS_PRESET_MAP[resolvedLayoutKey];
}

function sanitizePx(value, limits, label) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    throw new Error(`El tamano ${label} debe ser numerico.`);
  }
  if (numericValue < limits.min || numericValue > limits.max) {
    throw new Error(
      `El tamano ${label} debe estar entre ${limits.min}px y ${limits.max}px.`
    );
  }
  return Math.round(numericValue);
}

function sanitizeInteger(value, limits, label) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    throw new Error(`${label} debe ser numerico.`);
  }
  if (numericValue < limits.min || numericValue > limits.max) {
    throw new Error(`${label} debe estar entre ${limits.min} y ${limits.max}.`);
  }
  return Math.round(numericValue);
}

function sanitizePagination(input = {}, defaults = {}) {
  ensurePlainObject(input, "pagination");

  return {
    linesFirstPage: sanitizeInteger(
      input.linesFirstPage != null ? input.linesFirstPage : defaults.linesFirstPage,
      DTE_TEMPLATE_SIZE_LIMITS.paginationLines,
      "Lineas primera pagina"
    ),
    linesDetailPage: sanitizeInteger(
      input.linesDetailPage != null ? input.linesDetailPage : defaults.linesDetailPage,
      DTE_TEMPLATE_SIZE_LIMITS.paginationLines,
      "Lineas paginas de detalle"
    ),
    linesLastPage: sanitizeInteger(
      input.linesLastPage != null ? input.linesLastPage : defaults.linesLastPage,
      DTE_TEMPLATE_SIZE_LIMITS.paginationLines,
      "Lineas ultima pagina"
    ),
    showLineGuide:
      typeof input.showLineGuide === "boolean"
        ? input.showLineGuide
        : defaults.showLineGuide,
    hideRelatedDocumentsPreview:
      typeof input.hideRelatedDocumentsPreview === "boolean"
        ? input.hideRelatedDocumentsPreview
        : defaults.hideRelatedDocumentsPreview,
  };
}

function sanitizeBooleanMap(input = {}, defaults = {}, label) {
  ensurePlainObject(input, label);
  const output = { ...defaults };

  Object.keys(input).forEach((key) => {
    if (typeof input[key] !== "boolean") {
      throw new Error(`El valor ${label}.${key} debe ser booleano.`);
    }
    output[key] = input[key];
  });

  return output;
}

function sanitizeLabels(input = {}, defaults = {}) {
  ensurePlainObject(input, "labels");
  const output = { ...defaults };

  Object.keys(input).forEach((key) => {
    const value = input[key];
    if (value == null || value === "") {
      output[key] = defaults[key] || "";
      return;
    }

    if (typeof value !== "string") {
      throw new Error(`La etiqueta ${key} debe ser texto.`);
    }

    const trimmed = value.trim();
    if (trimmed.length > 60) {
      throw new Error(`La etiqueta ${key} supera la longitud permitida.`);
    }

    output[key] = trimmed || defaults[key] || "";
  });

  return output;
}

export function validateAndResolveDteTemplateSettings(settings, layoutKey = "estandar") {
  const defaults = getDefaultDteTemplateSettings(layoutKey);
  const input = settings == null ? {} : settings;
  ensurePlainObject(input, "settings");

  const themeInput = input.theme || {};
  const visibilityInput = input.visibility || {};

  if (themeInput && (typeof themeInput !== "object" || Array.isArray(themeInput))) {
    throw new Error("theme debe ser un objeto valido.");
  }

  if (
    visibilityInput &&
    (typeof visibilityInput !== "object" || Array.isArray(visibilityInput))
  ) {
    throw new Error("visibility debe ser un objeto valido.");
  }

  const titleFontSizePreset = sanitizePreset(
    themeInput.titleFontSizePreset || defaults.theme.titleFontSizePreset,
    defaults.theme.titleFontSizePreset
  );
  const textFontSizePreset = sanitizePreset(
    themeInput.textFontSizePreset || defaults.theme.textFontSizePreset,
    defaults.theme.textFontSizePreset
  );
  const detailFontSizePreset = sanitizePreset(
    themeInput.detailFontSizePreset || defaults.theme.detailFontSizePreset,
    defaults.theme.detailFontSizePreset
  );
  const cardBorderRadiusPreset = sanitizeRadiusPreset(
    themeInput.cardBorderRadiusPreset || defaults.theme.cardBorderRadiusPreset,
    defaults.theme.cardBorderRadiusPreset
  );
  const tableBorderRadiusPreset = sanitizeRadiusPreset(
    themeInput.tableBorderRadiusPreset || defaults.theme.tableBorderRadiusPreset,
    defaults.theme.tableBorderRadiusPreset
  );
  const cardRadiusMap = getRadiusPresetMap("card", layoutKey);
  const tableRadiusMap = getRadiusPresetMap("table", layoutKey);

  return {
    theme: {
      headerColor: themeInput.headerColor
        ? sanitizeColor(themeInput.headerColor)
        : defaults.theme.headerColor,
      gradientColor: themeInput.gradientColor
        ? sanitizeColor(themeInput.gradientColor)
        : defaults.theme.gradientColor,
      gradientStrength: sanitizePx(
        themeInput.gradientStrength != null
          ? themeInput.gradientStrength
          : defaults.theme.gradientStrength,
        DTE_TEMPLATE_SIZE_LIMITS.gradient,
        "de degradado"
      ),
      bannerFullPage:
        typeof themeInput.bannerFullPage === "boolean"
          ? themeInput.bannerFullPage
          : defaults.theme.bannerFullPage,
      hideAdvertising:
        typeof themeInput.hideAdvertising === "boolean"
          ? themeInput.hideAdvertising
          : defaults.theme.hideAdvertising,
      hideAdvertisingOnPrint:
        typeof themeInput.hideAdvertisingOnPrint === "boolean"
          ? themeInput.hideAdvertisingOnPrint
          : defaults.theme.hideAdvertisingOnPrint,
      hideAdvertisingOnPdfDownload:
        typeof themeInput.hideAdvertisingOnPdfDownload === "boolean"
          ? themeInput.hideAdvertisingOnPdfDownload
          : defaults.theme.hideAdvertisingOnPdfDownload,
      tableBorderColor: themeInput.tableBorderColor
        ? sanitizeColor(themeInput.tableBorderColor)
        : defaults.theme.tableBorderColor,
      cardBorderRadiusPreset,
      cardBorderRadiusPx: sanitizePx(
        themeInput.cardBorderRadiusPx != null
          ? themeInput.cardBorderRadiusPx
          : cardRadiusMap[cardBorderRadiusPreset],
        DTE_TEMPLATE_SIZE_LIMITS.radius,
        "de borde de tarjetas"
      ),
      tableBorderRadiusPreset,
      tableBorderRadiusPx: sanitizePx(
        themeInput.tableBorderRadiusPx != null
          ? themeInput.tableBorderRadiusPx
          : tableRadiusMap[tableBorderRadiusPreset],
        DTE_TEMPLATE_SIZE_LIMITS.radius,
        "de borde de tablas"
      ),
      titleFontSizePreset,
      titleFontSizePx: sanitizePx(
        themeInput.titleFontSizePx != null
          ? themeInput.titleFontSizePx
          : DTE_TEMPLATE_TITLE_FONT_SIZE_MAP[titleFontSizePreset],
        DTE_TEMPLATE_SIZE_LIMITS.title,
        "de titulos"
      ),
      textFontSizePreset,
      textFontSizePx: sanitizePx(
        themeInput.textFontSizePx != null
          ? themeInput.textFontSizePx
          : DTE_TEMPLATE_TEXT_FONT_SIZE_MAP[textFontSizePreset],
        DTE_TEMPLATE_SIZE_LIMITS.text,
        "de texto"
      ),
      detailFontSizePreset,
      detailFontSizePx: sanitizePx(
        themeInput.detailFontSizePx != null
          ? themeInput.detailFontSizePx
          : DTE_TEMPLATE_DETAIL_FONT_SIZE_MAP[detailFontSizePreset],
        DTE_TEMPLATE_SIZE_LIMITS.detail,
        "de detalle"
      ),
    },
    pagination: sanitizePagination(input.pagination || {}, defaults.pagination),
    visibility: {
      issuer: sanitizeBooleanMap(
        visibilityInput.issuer || {},
        defaults.visibility.issuer,
        "visibility.issuer"
      ),
      receiver: sanitizeBooleanMap(
        visibilityInput.receiver || {},
        defaults.visibility.receiver,
        "visibility.receiver"
      ),
      document: sanitizeBooleanMap(
        visibilityInput.document || {},
        defaults.visibility.document,
        "visibility.document"
      ),
      detail: sanitizeBooleanMap(
        visibilityInput.detail || {},
        defaults.visibility.detail,
        "visibility.detail"
      ),
      totals: sanitizeBooleanMap(
        visibilityInput.totals || {},
        defaults.visibility.totals,
        "visibility.totals"
      ),
    },
    labels: sanitizeLabels(input.labels || {}, defaults.labels),
  };
}

export function safeResolveDteTemplateSettings(settings, layoutKey = "estandar") {
  try {
    return validateAndResolveDteTemplateSettings(settings, layoutKey);
  } catch (error) {
    return getDefaultDteTemplateSettings(layoutKey);
  }
}

export function isDteTemplateFieldVisible(
  settings,
  section,
  key,
  fallback = true,
  layoutKey = "estandar"
) {
  const resolvedSettings = safeResolveDteTemplateSettings(settings, layoutKey);
  const sectionData = resolvedSettings.visibility[section] || {};
  return typeof sectionData[key] === "boolean" ? sectionData[key] : fallback;
}

export function getDteTemplateLabel(
  settings,
  key,
  fallback = "",
  layoutKey = "estandar"
) {
  const resolvedSettings = safeResolveDteTemplateSettings(settings, layoutKey);
  return resolvedSettings.labels[key] || fallback;
}

export function isDteTemplateBannerFullPage(settings, layoutKey = "estandar") {
  const resolvedSettings = safeResolveDteTemplateSettings(settings, layoutKey);
  return Boolean(
    resolvedSettings.theme &&
      !resolvedSettings.theme.hideAdvertising &&
      resolvedSettings.theme.bannerFullPage
  );
}

export function isDteTemplateAdvertisingHidden(settings, layoutKey = "estandar") {
  const resolvedSettings = safeResolveDteTemplateSettings(settings, layoutKey);
  return Boolean(resolvedSettings.theme && resolvedSettings.theme.hideAdvertising);
}

export function isDteTemplateHideAdvertisingOnPrint(settings, layoutKey = "estandar") {
  const resolvedSettings = safeResolveDteTemplateSettings(settings, layoutKey);
  return Boolean(
    resolvedSettings.theme && resolvedSettings.theme.hideAdvertisingOnPrint
  );
}

export function isDteTemplateHideAdvertisingOnPdfDownload(
  settings,
  layoutKey = "estandar"
) {
  const resolvedSettings = safeResolveDteTemplateSettings(settings, layoutKey);
  return Boolean(
    resolvedSettings.theme && resolvedSettings.theme.hideAdvertisingOnPdfDownload
  );
}

function normalizeHexColor(hexColor) {
  const color = sanitizeColor(hexColor);
  if (color.length === 4) {
    return `#${color[1]}${color[1]}${color[2]}${color[2]}${color[3]}${color[3]}`;
  }
  return color;
}

function colorToRgba(hexColor, alpha = 1) {
  const hex = normalizeHexColor(hexColor).replace("#", "");
  const red = parseInt(hex.slice(0, 2), 16);
  const green = parseInt(hex.slice(2, 4), 16);
  const blue = parseInt(hex.slice(4, 6), 16);
  const normalizedAlpha = Math.max(0, Math.min(1, Number(alpha)));

  return `rgba(${red}, ${green}, ${blue}, ${normalizedAlpha.toFixed(3)})`;
}

function mixColors(colorA, colorB, weight = 0.5) {
  const a = normalizeHexColor(colorA);
  const b = normalizeHexColor(colorB);
  const amount = Math.max(0, Math.min(1, Number(weight)));

  const parseChannel = (hex, offset) => parseInt(hex.slice(offset, offset + 2), 16);
  const channelToHex = (value) => value.toString(16).padStart(2, "0");

  const mixed = [1, 3, 5]
    .map((offset) => {
      const left = parseChannel(a, offset);
      const right = parseChannel(b, offset);
      return Math.round(left + ((right - left) * amount));
    })
    .map(channelToHex)
    .join("");

  return `#${mixed.toUpperCase()}`;
}

function getContrastColor(backgroundColor) {
  const hex = normalizeHexColor(backgroundColor).replace("#", "");
  const red = parseInt(hex.slice(0, 2), 16);
  const green = parseInt(hex.slice(2, 4), 16);
  const blue = parseInt(hex.slice(4, 6), 16);
  const brightness = ((red * 299) + (green * 587) + (blue * 114)) / 1000;
  return brightness > 150 ? "#1B2A3A" : "#FFFFFF";
}

export function mapDteTemplateSettingsToTheme(
  settings,
  baseTheme = {},
  layoutKey = "estandar"
) {
  const resolvedSettings = safeResolveDteTemplateSettings(settings, layoutKey);
  const headerColor = resolvedSettings.theme.headerColor;
  const gradientColor = resolvedSettings.theme.gradientColor || headerColor;
  const gradientStrength = Math.max(
    0,
    Math.min(100, Number(resolvedSettings.theme.gradientStrength) || 0)
  );
  const gradientAmount = gradientStrength / 100;

  return {
    ...baseTheme,
    accent: baseTheme.accent || "#d9a441",
    text: baseTheme.text || "#1b2a3a",
    muted: baseTheme.muted || "#6c7b8a",
    brand: headerColor,
    brandContrast: getContrastColor(headerColor),
    surface: mixColors(headerColor, "#FFFFFF", 0.9),
    border: mixColors(headerColor, "#FFFFFF", 0.76),
    tableBorderColor: resolvedSettings.theme.tableBorderColor,
    cardBorderRadiusPx: resolvedSettings.theme.cardBorderRadiusPx,
    tableBorderRadiusPx: resolvedSettings.theme.tableBorderRadiusPx,
    gradientColor,
    gradientStrength,
    cardHeaderGradientStrong: colorToRgba(gradientColor, 0.42 * gradientAmount),
    cardHeaderGradientSoft: colorToRgba(gradientColor, 0.06 * gradientAmount),
    titleFontSizePx: resolvedSettings.theme.titleFontSizePx,
    textFontSizePx: resolvedSettings.theme.textFontSizePx,
    detailFontSizePx: resolvedSettings.theme.detailFontSizePx,
  };
}

export function updateTemplateFontPreset(settings, fontType, preset) {
  const resolved = safeResolveDteTemplateSettings(settings);
  const nextPreset = sanitizePreset(preset, resolved.theme[`${fontType}FontSizePreset`]);
  const sizeMap =
    fontType === "title"
      ? DTE_TEMPLATE_TITLE_FONT_SIZE_MAP
      : fontType === "detail"
        ? DTE_TEMPLATE_DETAIL_FONT_SIZE_MAP
        : DTE_TEMPLATE_TEXT_FONT_SIZE_MAP;

  resolved.theme[`${fontType}FontSizePreset`] = nextPreset;
  resolved.theme[`${fontType}FontSizePx`] = sizeMap[nextPreset];
  return resolved;
}

export function updateTemplateFontPx(settings, fontType, value) {
  const resolved = safeResolveDteTemplateSettings(settings);
  const limits =
    fontType === "title"
      ? DTE_TEMPLATE_SIZE_LIMITS.title
      : fontType === "detail"
        ? DTE_TEMPLATE_SIZE_LIMITS.detail
        : DTE_TEMPLATE_SIZE_LIMITS.text;

  const numericValue = Number(value);
  const clampedValue = Math.min(
    limits.max,
    Math.max(limits.min, Number.isFinite(numericValue) ? numericValue : limits.min)
  );

  resolved.theme[`${fontType}FontSizePx`] = Math.round(clampedValue);
  return resolved;
}

export function updateTemplateRadiusPreset(settings, radiusType, preset, layoutKey = "estandar") {
  const resolved = safeResolveDteTemplateSettings(settings, layoutKey);
  const presetKey = sanitizeRadiusPreset(
    preset,
    resolved.theme[`${radiusType}BorderRadiusPreset`]
  );
  const radiusMap = getRadiusPresetMap(radiusType, layoutKey);

  resolved.theme[`${radiusType}BorderRadiusPreset`] = presetKey;
  resolved.theme[`${radiusType}BorderRadiusPx`] = radiusMap[presetKey];
  return resolved;
}

export function updateTemplateRadiusPx(settings, radiusType, value, layoutKey = "estandar") {
  const resolved = safeResolveDteTemplateSettings(settings, layoutKey);
  const numericValue = Number(value);
  const clampedValue = Math.min(
    DTE_TEMPLATE_SIZE_LIMITS.radius.max,
    Math.max(
      DTE_TEMPLATE_SIZE_LIMITS.radius.min,
      Number.isFinite(numericValue) ? numericValue : DTE_TEMPLATE_SIZE_LIMITS.radius.min
    )
  );

  resolved.theme[`${radiusType}BorderRadiusPx`] = Math.round(clampedValue);
  return resolved;
}
